import { dateFromKey, dateKeyOf } from "./schedule";

/**
 * 日本の祝日判定。現行法(振替休日・国民の休日を含む)に基づく計算式。
 * 春分・秋分は天文計算の近似式(1980〜2099年で有効)を使う。
 * 2020年より前の祝日(天皇誕生日が12/23だった時代、五輪特例による移動等)は
 * 対象外 ― カレンダーは現在・未来の予定が中心のため、過去の細かな正確性は求めない。
 */

function nthMonday(year: number, month: number, nth: number): Date {
  const first = new Date(year, month, 1);
  const offsetToMonday = (8 - first.getDay()) % 7;
  return new Date(year, month, 1 + offsetToMonday + (nth - 1) * 7);
}

function equinoxDay(year: number, season: "spring" | "autumn"): number {
  const base = season === "spring" ? 20.8431 : 23.2488;
  return Math.floor(base + 0.242194 * (year - 1980) - Math.floor((year - 1980) / 4));
}

function baseHolidaysOf(year: number): Map<string, string> {
  const map = new Map<string, string>();
  const add = (date: Date, name: string) => {
    map.set(dateKeyOf(date), name);
  };

  add(new Date(year, 0, 1), "元日");
  add(nthMonday(year, 0, 2), "成人の日");
  add(new Date(year, 1, 11), "建国記念の日");
  add(new Date(year, 1, 23), "天皇誕生日");
  add(new Date(year, 2, equinoxDay(year, "spring")), "春分の日");
  add(new Date(year, 3, 29), "昭和の日");
  add(new Date(year, 4, 3), "憲法記念日");
  add(new Date(year, 4, 4), "みどりの日");
  add(new Date(year, 4, 5), "こどもの日");
  add(nthMonday(year, 6, 3), "海の日");
  add(new Date(year, 7, 11), "山の日");
  add(nthMonday(year, 8, 3), "敬老の日");
  add(new Date(year, 8, equinoxDay(year, "autumn")), "秋分の日");
  add(nthMonday(year, 9, 2), "スポーツの日");
  add(new Date(year, 10, 3), "文化の日");
  add(new Date(year, 10, 23), "勤労感謝の日");
  return map;
}

/** 祝日に挟まれた平日(日曜以外)を「国民の休日」として休みにする */
function addNationalHolidayGapDays(map: Map<string, string>, year: number): void {
  for (let month = 0; month < 12; month++) {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const key = dateKeyOf(date);
      if (map.has(key) || date.getDay() === 0) continue;
      const prevKey = dateKeyOf(new Date(year, month, day - 1));
      const nextKey = dateKeyOf(new Date(year, month, day + 1));
      if (map.has(prevKey) && map.has(nextKey)) {
        map.set(key, "国民の休日");
      }
    }
  }
}

/** 祝日が日曜のとき、後続の祝日でない直近の日を振替休日にする */
function addSubstituteHolidays(map: Map<string, string>): void {
  const sundayHolidays = [...map.entries()].filter(
    ([key, name]) => name !== "振替休日" && dateFromKey(key).getDay() === 0,
  );
  for (const [key] of sundayHolidays) {
    let candidate = dateFromKey(key);
    do {
      candidate = new Date(candidate.getFullYear(), candidate.getMonth(), candidate.getDate() + 1);
    } while (map.has(dateKeyOf(candidate)));
    map.set(dateKeyOf(candidate), "振替休日");
  }
}

function holidaysOfYear(year: number): Map<string, string> {
  const map = baseHolidaysOf(year);
  addNationalHolidayGapDays(map, year);
  addSubstituteHolidays(map);
  return map;
}

const yearCache = new Map<number, Map<string, string>>();

function cachedHolidaysOfYear(year: number): Map<string, string> {
  let map = yearCache.get(year);
  if (map === undefined) {
    map = holidaysOfYear(year);
    yearCache.set(year, map);
  }
  return map;
}

/** 祝日名。祝日でなければ null */
export function japaneseHolidayNameOf(date: Date): string | null {
  return cachedHolidaysOfYear(date.getFullYear()).get(dateKeyOf(date)) ?? null;
}

export function isJapaneseHoliday(date: Date): boolean {
  return japaneseHolidayNameOf(date) !== null;
}
